import type { QueryClient } from '@tanstack/react-query';

/**
 * 「图纸归属图」缓存失效的**唯一出处**（v0.56）。
 *
 * 需求（用户 2026-10-16 原话）：
 *   「零件主数据那里的文件增减删除，在作业指导书那里能不能同步」
 *   「零件主数据归类到文件夹了，图纸跟作业指导书那里没统一变动，三者需要联动」
 *
 * ---------------------------------------------------------------------------
 * 为什么必须是一个**汇总入口**，而不是在各处零星 `invalidateQueries`
 * ---------------------------------------------------------------------------
 * 根因不是接口不对，是**前端缓存**：`main.tsx` 里全局
 *   `staleTime: 30_000` + `refetchOnWindowFocus: false`
 * ⇒ 在零件主数据改完关联，30 秒内切到作业指导书/SIP，React Query 直接拿旧缓存
 *   渲染，服务端其实早就对了。用户看到的是「改了没同步」。
 *
 * 而「一张图纸 / 一个零件 / 一个文件夹动了一下」会影响的下游有十来处查询 ——
 * 逐个去想「这个动作还该刷哪个键」，必然漏（本项目铁律：**同一语义几份实现**，
 * 迟早只改一处）。所以这里把「属于这张图的全部读侧」一次性列全，变更点只调
 * 一个函数：多刷几个键只是多几次 GET，漏刷一个键才是"界面和库不一致"。
 *
 * ★ 加新查询时的规矩：只要这个查询的**内容**会因为「图纸归户 / 零件归属 /
 *   文件夹层级 / 图纸存在与否」而变化，它的键就必须出现在下面这张表里。
 */

/** 受「图纸 ↔ 零件 ↔ 文件夹」变更影响的全部查询键（前缀匹配）。 */
const GRAPH_KEYS: readonly (readonly unknown[])[] = [
  // ---- 文件夹树本身（图纸区 + 零件主数据共用同一份缓存）----
  ['folders'],
  // ---- 图纸集合的几种视图 ----
  ['drawings'],        // 扁平全集（零件主数据底部"未归户"计数、识别完成刷新）
  ['all-drawings'],    // SIP 顶栏入口的「全库图纸」
  ['part-drawings'],   // SIP 零件入口的「本零件名下图纸」
  ['pm-drawings'],     // 零件编辑抽屉的「关联图纸」
  // ---- 单张图纸详情 ----
  // ★ 必须含 `['drawing']`：`folder_id` / 零件归属 / 文件名都在这条响应里，
  //   移动文件夹或改归户之后不失效，工作台右上角就还是旧夹子名。
  ['drawing'],
  // ---- 单张图纸的派生数据（图纸被删/被恢复时它们整体失效）----
  // 这几个键的内容都**只**取决于「这张图还在不在 + 它的特征是什么」，
  // 所以图纸存亡一变就必须跟着失效（`['drawing']` 一失效它们却留着，
  // 现象是「图纸没了、气泡还在画布上」）。
  ['balloons'],
  ['drawing-texts'],
  ['reports'],
  ['measure-preflight'],
  // ---- BOM 层级（子零件行里带图纸数）----
  ['pm-children'],
  ['pm-children-rows'],
  ['pm-parents'],
  // ---- 台账（列表带图纸数/文件夹归属）----
  ['part-master'],
  // 单条零件（SIP 表头 / 图纸侧「零件归属」chip 读的就是它）
  ['part-master-one'],
  // 图纸 ↔ 零件的自动匹配候选（图纸标题栏那一条「零件」栏）
  ['pm-match'],
  // ---- SIP（表头只读自台账；侧栏草稿数依赖图纸是否还在）----
  ['sip-docs'],
  ['sip-page'],
];

/**
 * 把上面那张表里的键全部失效（React Query 是**前缀匹配**，
 * 所以 `['part-drawings']` 一次带走 `['part-drawings', 12]` 等全部实例）。
 *
 * 用法：在任何会改变「图纸归户 / 零件归属 / 文件夹层级 / BOM 关系 / 图纸存亡」
 * 的 mutation `onSuccess` 里调一次。
 */
export function invalidateDrawingGraph(qc: QueryClient): void {
  for (const key of GRAPH_KEYS) {
    void qc.invalidateQueries({ queryKey: key });
  }
}
